import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_LABORATORYCONTRACT_LOAD_BY_LABORATORY_ID,
    ACTION_LABORATORYCONTRACT_LOAD_BY_ID,
    ACTION_LABORATORYCONTRACT_LOAD_FAILURE,
    ACTION_LABORATORYCONTRACT_LOAD_SUCCESS,

    ACTION_LABORATORYCONTRACT_SAVE,
    ACTION_LABORATORYCONTRACT_SAVE_SUCCESS,
    ACTION_LABORATORYCONTRACT_SAVE_FAILURE,

    ACTION_LABORATORYCONTRACT_VALIDATE,
    ACTION_LABORATORYCONTRACT_VALIDATE_SUCCESS,
    ACTION_LABORATORYCONTRACT_VALIDATE_FAILURE,

    ACTION_LABORATORYCONTRACT_CREATE,
    ACTION_LABORATORYCONTRACT_CREATE_SUCCESS,
    ACTION_LABORATORYCONTRACT_CREATE_FAILURE,

    ACTION_LABORATORYCONTRACT_SYNC,
    ACTION_LABORATORYCONTRACT_SYNC_SUCCESS,
    ACTION_LABORATORYCONTRACT_SYNC_FAILURE,

    ACTION_LABORATORYCONTRACT_CLEAR,
    ACTION_LABORATORYCONTRACT_FILTER,

    LaboratoryContractAction
} from '../actions/laboratoryContract';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ILaboratoryContractState {
    formData: Dtos.LaboratoryContract | Dtos.LaboratoryContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.LaboratoryContract[];
    formLength: number;
}

const initialState: ILaboratoryContractState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ILaboratoryContractState, _isLoading: boolean): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ILaboratoryContractState,
    formData: Dtos.LaboratoryContract | Dtos.LaboratoryContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ILaboratoryContractState, responseStatus: Dtos.ResponseStatus): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ILaboratoryContractState, formFilter: IFormFilter): ILaboratoryContractState {

    const items: Dtos.LaboratoryContract[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.agreementName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ILaboratoryContractState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ILaboratoryContractState, propertiesToClear?: string[]): ILaboratoryContractState {

    if (!propertiesToClear) {
        const newState: ILaboratoryContractState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ILaboratoryContractState, _isSaving: boolean): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ILaboratoryContractState,
    formData: Dtos.LaboratoryContract | Dtos.LaboratoryContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ILaboratoryContractState, responseStatus: Dtos.ResponseStatus): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ILaboratoryContractState, _isValidating: boolean): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ILaboratoryContractState,
    formData: Dtos.LaboratoryContract | Dtos.LaboratoryContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ILaboratoryContractState, responseStatus: Dtos.ResponseStatus): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ILaboratoryContractState, _isCreating: boolean): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ILaboratoryContractState,
    formData: Dtos.LaboratoryContract,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ILaboratoryContractState, responseStatus: Dtos.ResponseStatus): ILaboratoryContractState {
    const newState: ILaboratoryContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const LaboratoryContractReducer: Reducer<ILaboratoryContractState> = (state: ILaboratoryContractState = initialState, action: LaboratoryContractAction) => {
    switch (action.type) {
        case ACTION_LABORATORYCONTRACT_LOAD_BY_LABORATORY_ID:
        case ACTION_LABORATORYCONTRACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_LABORATORYCONTRACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_LABORATORYCONTRACT_SAVE:
            return saving(state, true);
        case ACTION_LABORATORYCONTRACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_VALIDATE:
            return validating(state, true);
        case ACTION_LABORATORYCONTRACT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_CREATE:
            return creating(state, true);
        case ACTION_LABORATORYCONTRACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_LABORATORYCONTRACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_FILTER:
            return filter(state, action.formFilter);
        case ACTION_LABORATORYCONTRACT_SYNC:
            return loading(state, true);
        case ACTION_LABORATORYCONTRACT_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_LABORATORYCONTRACT_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default LaboratoryContractReducer;